import { fmtDay } from "@/lib/time";
import React from "react";
import { SeatingBadge } from "@/components/Seating";
import { Check, MoreVertical, Phone, PhoneOff, Users, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui-kit";
import { WaMenu, LastContactNote } from "@/components/WhatsAppContact";
import { BookingStatusPill, DeadlineNote } from "@/components/BookingStatus";
import { fmtMoney } from "@/components/EventCard";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Booking, WaTemplate } from "@/types";

export type VenueAction = "confirm" | "reject" | "unable" | "cancel" | "approveChange" | "rejectChange";

const Btn = ({ id, onClick, children, tone = "plain", grow = false }: { id: string; onClick: () => void; children: React.ReactNode; tone?: "go" | "plain" | "bad"; grow?: boolean }) => (
  <button data-testid={id} onClick={onClick}
    className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1 active:scale-95 transition-transform ${grow ? "flex-1 min-w-0" : "shrink-0"} ${tone === "go" ? "bg-emerald-600 text-white hover:bg-emerald-700" : tone === "bad" ? "bg-primary/10 text-primary" : "bg-accent"}`}>
    {children}
  </button>
);

const Actions = ({ b, act, templates, onContact }: { b: Booking; act: (a: VenueAction) => () => void; templates?: WaTemplate[]; onContact?: (b: Booking, tpl: WaTemplate) => void }) => {
  const { t, lang } = usePrefs();
  const cr = b.change_request?.status === "pending";
  const live = (b.status === "pending" || b.status === "confirmed") && !b.checked_in;
  const contact = live && b.customer_phone && (
    <>
      <a href={`tel:${b.customer_phone}`} aria-label={b.customer_phone} data-testid={`venue-call-${b.booking_id}`} className="h-9 w-9 shrink-0 grid place-items-center rounded-full border bg-background hover:bg-accent"><Phone className="h-4 w-4" /></a>
      {!!templates?.length && onContact && <WaMenu b={b} templates={templates} onContact={onContact} compact />}
    </>
  );
  if (!contact && b.status !== "pending" && !cr && !(b.status === "confirmed" && live)) return null;
  return (
    <div className="flex items-center gap-2 pt-1">
      {contact}
      {b.status === "pending" && <>
        <Btn id={`venue-confirm-${b.booking_id}`} onClick={act("confirm")} tone="go" grow><Check className="h-4 w-4" />{t("b5.confirm")}</Btn>
        <Btn id={`venue-reject-${b.booking_id}`} onClick={act("reject")} tone="bad" grow><X className="h-4 w-4" />{t("b5.reject")}</Btn>
        <DropdownMenu dir={lang === "ar" ? "rtl" : "ltr"}>
          <DropdownMenuTrigger asChild>
            <button data-testid={`venue-booking-more-${b.booking_id}`} aria-label="more" className="h-9 w-9 shrink-0 grid place-items-center rounded-full hover:bg-accent"><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl p-1.5">
            <DropdownMenuItem data-testid={`venue-unable-${b.booking_id}`} onSelect={act("unable")} className="rounded-xl gap-2"><PhoneOff className="h-4 w-4" />{t("b5.unable")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </>}
      {cr && <>
        <Btn id={`venue-approve-change-${b.booking_id}`} onClick={act("approveChange")} tone="go" grow>{t("b5.approveChange")}</Btn>
        <Btn id={`venue-reject-change-${b.booking_id}`} onClick={act("rejectChange")} grow>{t("b5.rejectChange")}</Btn>
      </>}
      {b.status === "confirmed" && live && !cr && <Btn id={`venue-booking-cancel-${b.booking_id}`} onClick={act("cancel")} tone="bad">{t("bookings.cancel")}</Btn>}
    </div>
  );
};

export const VenueBookingCard = ({ b, onAction, templates, onContact }: { b: Booking; onAction?: (b: Booking, a: VenueAction) => void; templates?: WaTemplate[]; onContact?: (b: Booking, tpl: WaTemplate) => void }) => {
  const { t, lang } = usePrefs();
  const act = (a: VenueAction) => () => onAction?.(b, a);
  const cr = b.change_request?.status === "pending" ? b.change_request : null;
  const note = b.decision_note || b.external_note || (b.cancel_reason === "event_cancelled" ? t("b5.eventCancelled") : b.cancel_reason);
  return (
    <div data-testid={`venue-booking-${b.booking_id}`} className={`rounded-3xl border bg-card p-3.5 space-y-1.5 min-w-0 ${b.status === "pending" ? "border-amber-500/50 ring-1 ring-amber-500/20" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold truncate min-w-0" data-testid={`venue-booking-customer-${b.booking_id}`}>{b.customer_name}</p>
        <div className="flex items-center gap-1 shrink-0">
          {b.source === "external" && <span className="rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 px-2 py-0.5 text-[10px] font-bold">{t("b5.sourceExternal")}</span>}
          <BookingStatusPill b={b} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground truncate">
        {lang === "ar" ? b.event_title_ar || b.venue_name_ar : b.event_title_en || b.venue_name_en} · {fmtDay(b.date, lang)}
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1 font-semibold" data-testid={`venue-booking-guests-${b.booking_id}`}><Users className="h-3.5 w-3.5" />{b.guests}</span>
        {b.price_snapshot?.total && <b dir="ltr">{fmtMoney(b.price_snapshot.total)}</b>}
        <SeatingBadge s={b.seating} testId={`booking-seating-${b.booking_id}`} />
        {b.code && <span className="font-mono font-bold text-muted-foreground" dir="ltr">{b.code}</span>}
        <DeadlineNote b={b} venue />
      </div>
      {note && <p className="text-xs text-muted-foreground line-clamp-1" title={note}>{note}</p>}
      {cr && <p className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-1.5 text-xs font-semibold" data-testid={`venue-change-request-${b.booking_id}`}>
        {t("b5.changeReq").replace("{a}", String(cr.from_guests)).replace("{b}", String(cr.guests))}{cr.total && <> · <bdi dir="ltr">{fmtMoney(cr.total)}</bdi></>}
      </p>}
      <LastContactNote b={b} />
      {onAction && <Actions b={b} act={act} templates={templates} onContact={onContact} />}
    </div>
  );
};
